import { useState, type FormEvent } from 'react';
import { useSessao } from '../autenticacao/ContextoDeSessao.js';
import type { OperadorLogado } from '../autenticacao/cognito.js';

/**
 * Entrada do cockpit. Dois estados: credenciais e, no primeiro acesso, definição da
 * senha definitiva a partir da senha temporária enviada por e-mail pelo Cognito.
 */
export function Login() {
  const { entrar, confirmarEntrada } = useSessao();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [concluirTroca, setConcluirTroca] = useState<
    ((novaSenha: string) => Promise<OperadorLogado>) | null
  >(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  async function aoEntrar(evento: FormEvent) {
    evento.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      const resultado = await entrar(email.trim(), senha);
      if (resultado.tipo === 'nova-senha') {
        // Guardado em função para o React não confundir com atualização funcional.
        setConcluirTroca(() => resultado.concluir);
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível entrar.');
    } finally {
      setEnviando(false);
    }
  }

  async function aoTrocarSenha(evento: FormEvent) {
    evento.preventDefault();
    setErro('');

    if (novaSenha !== confirmacao) {
      setErro('As duas senhas precisam ser iguais.');
      return;
    }

    setEnviando(true);
    try {
      const operador = await concluirTroca!(novaSenha);
      confirmarEntrada(operador);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível definir a senha.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="tela-login">
      <div className="caixa-login">
        <div className="marca">A PONTE</div>
        <div className="sub">Cockpit de vendas · cadeiras</div>
        <div className="filete" />

        {concluirTroca ? (
          <form onSubmit={aoTrocarSenha}>
            <p className="dica" style={{ marginBottom: 18 }}>
              Primeiro acesso. Defina a senha definitiva: ao menos 12 caracteres, com maiúscula,
              minúscula, número e símbolo.
            </p>
            <div className="campo">
              <label htmlFor="nova">Nova senha</label>
              <input
                id="nova"
                type="password"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="campo">
              <label htmlFor="confirmacao">Repita a nova senha</label>
              <input
                id="confirmacao"
                type="password"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <button className="bt cheio" type="submit" disabled={enviando}>
              {enviando ? 'Salvando…' : 'Definir senha e entrar'}
            </button>
          </form>
        ) : (
          <form onSubmit={aoEntrar}>
            <div className="campo">
              <label htmlFor="email">E-mail</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@rrevela.com.br"
                autoComplete="username"
                required
              />
            </div>
            <div className="campo">
              <label htmlFor="senha">Senha</label>
              <input
                id="senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="current-password"
                required
              />
              <div className="dica">
                O acesso é nominal e registra quem emitiu cada documento e cada cobrança.
              </div>
            </div>
            <button className="bt cheio" type="submit" disabled={enviando}>
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>
          </form>
        )}

        {erro && (
          <div className="aviso erro" style={{ marginTop: 16 }}>
            {erro}
          </div>
        )}
      </div>
    </div>
  );
}
